import { motion } from "framer-motion";
import { ArrowRight, Building2, Target, Eye, Sparkles, Award } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import MagneticButton from "../components/MagneticButton";
import SectionHeading from "../components/SectionHeading";
import { company, stats } from "../data/site";

const heroImg =
  "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85";

const valueProps = [
  {
    title: "End-to-End Solutions",
    desc: "From feasibility to occupancy, a single accountable team owns the entire approval pipeline.",
    icon: Building2,
  },
  {
    title: "Proactive Due Diligence",
    desc: "We detect Master Plan roads, RDP, HT lines, Nalas and prohibited survey numbers before you commit.",
    icon: Target,
  },
  {
    title: "Global Reach, Local Expertise",
    desc: "Trusted by 100+ builders and architects across Hyderabad. Deep ULB rules knowledge.",
    icon: Sparkles,
  },
  {
    title: "Creative Team, Seamless Execution",
    desc: "Innovative pre-DCR, AutoDCR, CAD design and application teams working as one.",
    icon: Award,
  },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About Hoflich"
        title="A decade of decoding Hyderabad’s building landscape."
        description={`Founded in ${company.founded} by ${company.founder}, Hoflich has rapidly evolved into a leading expert in helping landowners build on their own land. Under Vivek's able leadership, the company has successfully navigated the complex landscape of independent land development for over a decade.`}
        image={heroImg}
        breadcrumbs={["Home", "About"]}
      />

      {/* Founder story */}
      <section className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <p className="eyebrow">Founder</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05]">
                {company.founder}
              </h2>
              <p className="text-sm tracking-[0.18em] uppercase text-white/45 mt-3">
                Founder & Managing Director
              </p>

              <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8 }}
                className="mt-10 relative pl-6 border-l-2 border-[#C96A2B]"
              >
                <p className="font-display text-xl md:text-2xl leading-snug">
                  “Let us be your catalyst for success in Hyderabad’s dynamic
                  building landscape.”
                </p>
                <footer className="mt-4 text-xs text-white/45 tracking-[0.18em] uppercase">
                  — {company.founder}, Founder
                </footer>
              </motion.blockquote>
            </div>

            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="The Story"
                title="From a single approval to Hyderabad’s most trusted liaison."
                description={`Hoflich was founded in ${company.founded} in Hyderabad by ${company.founder} — driven by a singular insight: builders deserve a partner that owns the entire approval journey, not just one step of it. A decade later, that conviction has become a decade-long track record across every major Urban Local Body in the state.`}
                testId="about-founder-heading"
              />

              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((s) => (
                  <div
                    key={s.label}
                    className="p-5 rounded-xl border border-white/8 bg-white/[0.02]"
                  >
                    <p className="font-display text-3xl md:text-4xl">
                      {s.value}
                      <span className="text-[#C96A2B]">{s.suffix}</span>
                    </p>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-white/45 mt-2">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0c]">
        <div className="absolute inset-0 blueprint-grid opacity-[0.04]" />
        <div className="relative max-w-7xl mx-auto grid md:grid-cols-2 gap-px bg-white/8 border border-white/8 rounded-3xl overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="bg-[#0F0F12] p-10 md:p-14"
          >
            <Eye className="text-[#C96A2B]" size={28} />
            <p className="eyebrow mt-6">Our Vision</p>
            <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
              To be Hyderabad’s most trusted partner for building on your own land.
            </h3>
            <p className="text-white/55 mt-6 leading-relaxed">
              We exist to remove regulatory friction from city-building. By compressing
              approval timelines with clarity and integrity, we help India’s most
              ambitious developers build the future faster.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-[#0F0F12] p-10 md:p-14"
          >
            <Target className="text-[#C96A2B]" size={28} />
            <p className="eyebrow mt-6">Our Mission</p>
            <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
              Streamline. Sanction. Sustain.
            </h3>
            <p className="text-white/55 mt-6 leading-relaxed">
              Dedicated to streamlining the approval process, ensuring timely project
              completion and fostering long-term relationships through transparent,
              proactive and technically sound liaison.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            eyebrow="What sets us apart"
            title="Four pillars that compound, project after project."
            align="center"
            testId="about-pillars-heading"
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {valueProps.map((v, i) => (
              <motion.div
                key={v.title}
                data-testid={`value-prop-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="card-ring relative group p-8 md:p-10 rounded-2xl border border-white/8 bg-[#0F0F12] hover:bg-[#15151a] transition-colors"
              >
                <v.icon size={26} className="text-[#C96A2B]" />
                <h3 className="font-display text-2xl md:text-3xl mt-6">{v.title}</h3>
                <p className="text-white/55 mt-3 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-36 px-6 md:px-12 bg-[#080808] overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[700px] orange-glow opacity-70" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="eyebrow">Ready to begin?</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl mt-6 leading-[1.05]">
            Let’s simplify your next approval.
          </h2>
          <p className="text-white/55 mt-6 max-w-2xl mx-auto">
            From a single Fire NOC to a complete township sanction — we’ll map the
            cleanest path forward.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <MagneticButton href="/consultation" testId="about-cta-consult">
              Book Consultation
              <ArrowRight size={18} />
            </MagneticButton>
            <MagneticButton href="/services" variant="ghost">
              Explore services
            </MagneticButton>
          </div>
        </div>
      </section>
    </main>
  );
}
